import { t } from "@lingui/core/macro";
import React, { useCallback, useMemo, useRef } from "react";

import type { ReactImageGalleryItem } from "react-image-gallery";
import * as styles from "./Slideshow.module.scss";
import { RemoveScroll } from "react-remove-scroll";
import {
  SlideshowWithVideoFragment,
  SlideshowOnlyImageAltFragment,
  SanityVideo,
  SlideshowOnlyImageFragment
} from "types/graphql-types";
import {
  Button,
  Icon,
  SanityImageType,
  ResponsiveSanityImage,
  PaginationButton
} from "@silversea-ssc/ui-components-react";
import clsx from "clsx";
import SlideshowVideo from "./SlideshowVideo";
import SlideshowImage from "./SlideshowImage";
import { convertImages } from "./utils";
import { IngoreErrorBoundary } from "../ErrorBoundary";
import ClientOnly from "components/ClientOnly";
import type ReactImageGallery from "react-image-gallery";

const ImageGallery = React.lazy(() => import("react-image-gallery"));

export type SlideshowItemImage =
  | SlideshowOnlyImageFragment
  | SlideshowOnlyImageAltFragment;

export type SlideshowItem = SlideshowItemImage | SlideshowWithVideoFragment;

type SlideshowVariant = "gray" | "white" | "dark";

export interface InternalSlideshowItemProps {
  key?: string;
  isFullscreen?: boolean;
  image?: SanityImageType;
  thumbnail?: SanityImageType;
  description?: string;
  video?: SanityVideo;
  isVideo?: boolean;
  fullScreenEnabled?: boolean;
  imageFit?: "contain" | "cover";
  imageAlt?: string;
}

export interface SlideshowProps {
  items: SlideshowItem[];
  className?: string;
  hideCaption?: boolean;
  sizes?: string;
  fullscreen?: boolean;
  variant?: SlideshowVariant;
  imageFit?: "contain" | "cover";
  fluidHeight?: boolean;
  dataContentName: string;
  roundedCorners?: boolean;
}

interface SlideshowState {
  isFullscreen: boolean;
  currentCaption?: string;
  items: InternalSlideshowItemProps[];
}

const Slideshow: React.FC<SlideshowProps> = ({
  items,
  className,
  hideCaption = false,
  fullscreen: enableFullscreen,
  imageFit = "cover",
  fluidHeight = false,
  dataContentName,
  variant,
  roundedCorners = true
}) => {
  const imageGallery = useRef<ReactImageGallery | null>(null);
  const convertedItems = useMemo(
    () => convertImages(items, hideCaption || false),
    [items, hideCaption]
  );
  const hasNavigation = convertedItems?.length > 1;
  const showCaption = hideCaption != true || hasNavigation;
  const [state, setState] = React.useState<Omit<SlideshowState, "items">>({
    isFullscreen: false,
    currentCaption: convertedItems[0]?.description
  });

  const _onScreenChange = useCallback((isFullscreen: boolean | undefined) => {
    setState((state) => ({ ...state, isFullscreen: !!isFullscreen }));
  }, []);

  const onClickSlide = useCallback(() => {
    if (enableFullscreen) imageGallery?.current?.fullScreen();
  }, [enableFullscreen]);

  const renderFullscreenButton = useCallback(
    (onClick: React.MouseEventHandler<HTMLElement>, isFullscreen: boolean) => {
      return !isFullscreen ? (
        <Button
          renderAs="button"
          className={styles.buttonFullscreen}
          onClick={onClick}
          iconName="expand"
          variant="secondary"
          dataContentPiece="fullscreen"
          aria-label={`Fullscreen`}
        ></Button>
      ) : (
        <Button
          renderAs="button"
          className={styles.buttonFullscreenClose}
          onClick={onClick}
          iconName="close"
          variant="secondary"
          dataContentPiece="fullscreen_close"
          aria-label={`Close`}
        ></Button>
      );
    },
    []
  );

  const renderThumbInner = useCallback((item) => {
    return (
      <div className={styles.imageGalleryThumbnailInner}>
        <ResponsiveSanityImage
          image={item.image}
          alt={item.thumbnailAlt}
          title={item.thumbnailTitle}
          draggable={false}
          className="image-gallery-thumbnail-image"
          defaultWidth={105}
          defaultHeight={70}
          loading="lazy"
        />
        {item.isVideo && (
          <Icon name="play" className={styles.thumbnailVideoIcon} size="2x" />
        )}
      </div>
    );
  }, []);

  const renderLeftNav = useCallback(
    (onClick: React.MouseEventHandler<HTMLElement>, disabled: boolean) => {
      return (
        <PaginationButton
          dataContentPiece="slideshow_prev_button"
          arrow="left"
          disabled={disabled}
          onClick={onClick}
          aria-label={t`Previous`}
          role="button"
          className={clsx([styles.arrowButton, styles.arrowButtonLeft])}
        />
      );
    },
    []
  );

  const renderRightNav = useCallback(
    (onClick: React.MouseEventHandler<HTMLElement>, disabled: boolean) => {
      return (
        <PaginationButton
          className={clsx([styles.arrowButton, styles.arrowButtonRight])}
          disabled={disabled}
          onClick={onClick}
          dataContentPiece="slideshow_next_button"
          aria-label={t`Next`}
          arrow="right"
          role="button"
        ></PaginationButton>
      );
    },
    []
  );

  const renderImage = useCallback(
    (_item) => {
      const item = _item as InternalSlideshowItemProps;
      const isFullscreen = state.isFullscreen;
      const { isVideo } = item;

      return isVideo ? (
        <SlideshowVideo
          key={item.key}
          image={item.image}
          description={item.description}
          video={item.video}
          isVideo
          imageAlt={item.imageAlt}
          isFullscreen={isFullscreen}
          fullScreenEnabled={enableFullscreen}
          imageFit={imageFit}
          onPlayClick={onClickSlide}
        />
      ) : (
        <SlideshowImage
          key={item.key}
          image={item.image}
          description={item.description}
          isVideo={false}
          fullScreenEnabled={item.fullScreenEnabled}
          imageAlt={item.imageAlt}
          isFullscreen={isFullscreen}
          imageFit={imageFit}
        />
      );
    },
    [enableFullscreen, imageFit, state.isFullscreen]
  );

  const onSlide = useCallback(
    (index: number) => {
      setState((state) => ({
        ...state,
        currentCaption: convertedItems[index]?.description
      }));
    },
    [convertedItems]
  );

  return (
    <div
      data-content-name={dataContentName}
      className={clsx(className, styles.slideshow, {
        [styles.slideshowFullscreen]: state.isFullscreen,
        [styles.slideshowHideNavigation]: !hasNavigation,
        [styles.slideshowHideCaption]: !showCaption,
        [styles.variantGray]: variant == "gray",
        [styles.variantDark]: variant == "dark",
        [styles.imageFitCover]: imageFit == "cover",
        [styles.imageFitContain]: imageFit == "contain",
        [styles.fluidHeight]: fluidHeight,
        [styles.roundedCorners]: roundedCorners
      })}
    >
      <ClientOnly>
        <IngoreErrorBoundary>
          <RemoveScroll enabled={state.isFullscreen && enableFullscreen}>
            <React.Suspense fallback={null}>
              <ImageGallery
                ref={imageGallery}
                items={
                  convertedItems as unknown as readonly ReactImageGalleryItem[]
                }
                showIndex
                showNav
                lazyLoad
                renderThumbInner={renderThumbInner}
                showThumbnails={state.isFullscreen && enableFullscreen}
                showPlayButton={false}
                onScreenChange={_onScreenChange}
                showFullscreenButton={enableFullscreen}
                renderFullscreenButton={renderFullscreenButton}
                renderLeftNav={renderLeftNav}
                renderRightNav={renderRightNav}
                renderItem={renderImage}
                indexSeparator={` ${t`count-separator-xx-of-xx`} `}
                onSlide={onSlide}
                disableSwipe={false}
                //onClick={onClickSlide}
              />
            </React.Suspense>
            <div className={styles.imageCaption}>{state.currentCaption}</div>
          </RemoveScroll>
        </IngoreErrorBoundary>
      </ClientOnly>
    </div>
  );
};
export default Slideshow;
